import { AutocompleteProps } from "@mui/material";
import { DefaultHandlers, TypedPathWrapper } from "typed-path";
import { FormikErrors, FormikProps } from "formik";
import { isUndefined } from "./identity";
import { TFunction } from "i18next";
import { TypedPathTo } from "./typedPath";
import lodashGet from "lodash/get";

export function setAndTouch<TValue, TFormState>(
  formik: FormikProps<TFormState>,
  path: TypedPathTo<TValue>,
  value: TValue
): void {
  formik.setFieldValue(path.$path, value);
  formik.setFieldTouched(path.$path, true);
}

export const numberOrEmptyString = (
  value: number | string | undefined
): "" | number => {
  if (isUndefined(value)) {
    return "";
  }
  if (typeof value === "string" && value === "") {
    return "";
  }
  return Number(value);
};

export const getAutocompleteText = (
  t: TFunction
): Pick<
  AutocompleteProps<any, any, any, any>,
  "loadingText" | "noOptionsText" | "getLimitTagsText"
> => ({
  loadingText: t("components.Loading.placeholder"),
  noOptionsText: t("utils.form.noOptions"),
});

/**
 * MUI doesn't let you use undefined as a "none" value for selects, only the
 * empty string and `null`. this method converts undefined values to something
 * that formik can use.
 */
export function undefinedToEmptyString(s: string | undefined): string {
  return s ?? "";
}

/**
 * MUI doesn't let you use undefined as a "none" value for selects, only the
 * empty string and `null`.  this method converts empty strings back to
 * undefined on the way out.
 */
export function emptyStringToUndefined(s: string): string | undefined {
  return s === "" ? undefined : s;
}

function relevantErrors<T>(
  errors: FormikErrors<T>,
  stepFields: TypedPathWrapper<unknown, DefaultHandlers>[] | undefined
): FormikErrors<T> {
  if (stepFields) {
    // cqb 2/26/2026: the one shortcoming of the type safety here is that
    // TypedPathWrapper doesn't (afaik) let you specify "any TypedPathWrapper in a
    // root-level type of T", so we can't verify that formikProps and stepFields
    // are actually related, and we have to cast away type safety here.  caveat
    // emptor.
    return Object.fromEntries(
      stepFields
        .map((path) => [path.$path as keyof T, lodashGet(errors, path.$path)])
        .filter(([_k, v]) => v !== undefined)
    );
  } else {
    return errors;
  }
}

export function validateThen<T>(
  formikProps: FormikProps<T>,
  onSuccess: () => void,
  onError?: () => void,
  stepFields?: TypedPathWrapper<unknown, DefaultHandlers>[]
): void {
  formikProps.validateForm().then((errObj) => {
    const relevant: FormikErrors<T> = relevantErrors(errObj, stepFields);
    if (Object.keys(relevant).length > 0) {
      for (const fieldName of Object.keys(relevant)) {
        formikProps.setFieldTouched(fieldName, true, true);
      }
      onError?.();
    } else {
      onSuccess();
    }
  });
}
